/**
 * Table sound-effect permission — the single place that decides whether a table
 * SFX is allowed to play for this player.
 *
 * Unity parity. `AudioManager.IsMutedForSoundType(bool isOwnSound)` is the
 * canonical implementation:
 *
 *   if (isGlobalMute) return true;                     // "Table sounds disabled"
 *   if (!isOwnSound && arePlayersMuted) return true;   // "Mute Other Players' Sounds"
 *   return false;
 *
 * and every call site passes `isOwnSound: IsPOVPlayer()` — i.e. "was this cue
 * triggered by the seat I am sitting in?". React had the same rule hand-copied
 * into Game/Bet/SeatTimer with slightly different spellings, and a few triggers
 * missed it entirely; this module is that rule, once.
 *
 * NOTE on the global mute: the React client enforces "Table sounds disabled" at
 * the Howler level (`Howler.mute(isMuted)` in GlobalContext), so callers do not
 * normally need to pass `tableSoundsEnabled`. It is accepted here so the helper
 * is self-contained and testable against the full Unity truth table.
 */

/**
 * Where a cue comes from.
 *
 * - `player` — triggered BY a seat (bet, call, check, fold, all-in, chips to
 *   pot, that seat's showdown flip, that seat's timeout). Muted when it belongs
 *   to somebody else and the player has opted out of opponent noise.
 * - `table`  — not attributable to a seat (community-card deals, dealer cues).
 *   Unity emits these from the POV player's own UIPlayer, so they survive the
 *   opponent mute; they stay audible here too.
 */
export type TableSfxOrigin = 'player' | 'table';

export type TableSfxRequest = {
  /** Defaults to `player` — the conservative choice: an unlabelled cue is gated. */
  origin?: TableSfxOrigin;
  /** Seat that triggered the cue. Unknown/absent reads as "not me". */
  actorSeatId?: number | string | null;
  /** This client's seat. `-1` while observing, so nothing reads as self. */
  currentPlayerSeatId?: number | string | null;
  /** "Table sounds disabled", inverted. Defaults true (Howler owns the global mute). */
  tableSoundsEnabled?: boolean;
  /** "Mute Other Players' Sounds". */
  muteOtherPlayersSounds: boolean;
  /**
   * Whether `muteOtherPlayersSounds` is a deliberate choice by the player rather
   * than the shipped default (TableSettings' `muteOtherPlayersSetByUser`).
   *
   * Only consulted for a viewer with no seat — see the guest branch below.
   * Defaults true so an unlabelled call obeys the setting as given.
   */
  muteOtherPlayersIsExplicit?: boolean;
};

/**
 * "We do not know this seat", as distinct from "this seat is not at the table".
 *
 * Cannot be expressed with `Number.isFinite` alone: `Number(null)` and
 * `Number('')` are both `0`, a perfectly finite seat number, so a null actor
 * would silently compare as seat 0 and be muted as an opponent.
 */
const isUnknownSeat = (v: number | string | null | undefined): boolean =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  !Number.isFinite(Number(v));

/**
 * A viewer with no seat of their own — a guest or an observer.
 *
 * `recomputeSeatDerivedFields` resolves the seat with `_.findIndex`, so it is
 * `-1` when the viewer is not at the table, and `0` in the initial state; the
 * reducer itself treats only `> 0` as seated. Either value means "not playing".
 *
 * An UNKNOWN seat is a different thing and is handled separately — see
 * `isUnknownSeat` and the attribution branch in `shouldPlayTableSfx`.
 */
const holdsNoSeat = (currentPlayerSeatId: number | string | null | undefined): boolean => {
  const seat = Number(currentPlayerSeatId);
  return Number.isFinite(seat) && seat <= 0;
};

/**
 * True when the cue may be played.
 *
 * The rule is "mute only what can be POSITIVELY attributed to another seat".
 * Anything unknown stays audible — the reverse (erring toward silence) caused a
 * run of bugs where a transient seat-resolution gap silenced the table outright,
 * including the player's own actions. Details on each branch below.
 */
export const shouldPlayTableSfx = ({
  origin = 'player',
  actorSeatId,
  currentPlayerSeatId,
  tableSoundsEnabled = true,
  muteOtherPlayersSounds,
  muteOtherPlayersIsExplicit = true
}: TableSfxRequest): boolean => {
  if (!tableSoundsEnabled) return false;
  if (!muteOtherPlayersSounds) return true;
  if (origin === 'table') return true;

  // Guests and observers, still on the DEFAULT, hear the whole table.
  //
  // "Mute Other Players' Sounds" presupposes the viewer IS one of the players:
  // it silences everyone else so you still hear yourself. Somebody with no seat
  // has no sounds of their own, so applying the mute silences the table
  // completely — they would watch a hand play out in total silence.
  //
  // Unity never hit this because it defaults the setting OFF. Skullcrusher
  // deliberately defaults it ON, so without this an observer's first impression
  // would be a silent table.
  //
  // But an observer who TOGGLES it on is asking for exactly that silence, so a
  // deliberate choice is always honoured — hence the explicitness check rather
  // than a blanket bypass.
  if (holdsNoSeat(currentPlayerSeatId) && !muteOtherPlayersIsExplicit) return true;

  // Mute ONLY when the cue can be POSITIVELY attributed to a different seat.
  //
  // This used to compare the two numbers directly, so an unknown seat on either
  // side (undefined / null / '' → NaN, and NaN !== NaN) fell through to "not me"
  // and was muted — "err toward silence". That is backwards: seat resolution can
  // legitimately be missing for a beat (`recomputeSeatDerivedFields` resolves it
  // by GUID, so it is unresolved until identity lands, and partial table state
  // renders without `currentPlayer` at all). Every such gap became TOTAL silence,
  // including the player's OWN actions — the setting turned a transient data hole
  // into no audio.
  //
  // Erring the other way is strictly safer: the worst case is hearing one
  // opponent cue you wanted muted, versus hearing nothing at all.
  if (isUnknownSeat(actorSeatId) || isUnknownSeat(currentPlayerSeatId)) return true;

  return Number(actorSeatId) === Number(currentPlayerSeatId);
};

/**
 * Betting-round hand phases during which a seat's action cue is emitted.
 *
 * Kept exactly as it was before this change (4..11): widening it would make
 * fold/check cues play where they never used to, an audible change for players
 * who leave the setting OFF.
 */
export const ACTION_SFX_PHASE_MIN = 4;
export const ACTION_SFX_PHASE_MAX = 11;

export const isActionSfxPhase = (hand: number | string): boolean => {
  const phase = Number(hand);
  return Number.isFinite(phase) && phase >= ACTION_SFX_PHASE_MIN && phase <= ACTION_SFX_PHASE_MAX;
};

/**
 * Community-card flip timing.
 *
 * `CommunityCard` holds each new board card face-down, waits
 * `COMMUNITY_FLIP_DELAY_S`, then collapses the back over
 * `COMMUNITY_FLIP_HALF_S` before the face swings in. So the card is not readable
 * until the sum of those — 800ms after it mounts.
 *
 * These DOCUMENT the flip; they are not applied to the cue. Delaying the cue onto
 * the reveal was tried and was the wrong direction — the reported symptom is the
 * sound arriving LATE, and on an all-in run-out the board does not flip at all
 * (HoldEmCards passes `skipFlip` once the hand passes 13), so there is no lead-in
 * to compensate for. The board cue is data-driven instead; see `countBoardCards`.
 *
 * Exported as the pieces (not just the total) so CommunityCard drives its gsap
 * timeline from these very numbers.
 */
export const COMMUNITY_FLIP_DELAY_S = 0.6;
export const COMMUNITY_FLIP_HALF_S = 0.2;
export const COMMUNITY_CARD_REVEAL_MS = (COMMUNITY_FLIP_DELAY_S + COMMUNITY_FLIP_HALF_S) * 1000;

/** The action cues a seat can emit during a betting round. */
export type ActionSfx = 'bet' | 'check' | 'fold';

export type ActionSfxRequest = {
  hand: number | string;
  /** game.lastplayer — the seat whose action this is. */
  lastplayer?: number | string | null;
  /** The p{seat}action value, any casing. */
  lastaction?: string | null;
  currentPlayerSeatId?: number | string | null;
  muteOtherPlayersSounds: boolean;
  tableSoundsEnabled?: boolean;
  /** See {@link TableSfxRequest.muteOtherPlayersIsExplicit}. */
  muteOtherPlayersIsExplicit?: boolean;
};

/**
 * Which action cue (if any) a table update should play — pure, so the audio
 * decision is unit-testable without mounting a table.
 *
 * Returns null when the phase is outside the betting rounds, when the action has
 * no cue, or when the cue belongs to another player and they are muted.
 */
export const resolveActionSfx = ({
  hand,
  lastplayer,
  lastaction,
  currentPlayerSeatId,
  muteOtherPlayersSounds,
  tableSoundsEnabled = true,
  muteOtherPlayersIsExplicit = true
}: ActionSfxRequest): ActionSfx | null => {
  if (!isActionSfxPhase(hand)) return null;

  const action = (lastaction ?? '').toLowerCase();
  let cue: ActionSfx | null = null;
  if (action === 'ante') cue = 'bet';
  else if (action === 'check') cue = 'check';
  else if (action === 'fold') cue = 'fold';
  if (!cue) return null;

  // A fold is only feedback for the seat that folded (EN-10558). At a full
  // ring most actions are folds, so the bell rang near-constantly for events
  // that tell the listener nothing — the most-reported sound complaint in
  // beta. Other seats' folds are silent OUTRIGHT, not behind the EN-10512
  // setting: even a player who wants opponents' chips and bets audible never
  // wants the fold bell.
  //
  // Deliberately the OPPOSITE polarity from shouldPlayTableSfx below. The
  // mute gate errs toward sound because a transient attribution gap silencing
  // the whole table was a real bug; here the stray sound IS the complaint, so
  // the fold cue plays only when POSITIVELY attributed to the viewer's own
  // seat — an occasionally missing own-fold cue is harmless, a stray bell is
  // not. Observers (no seat) fold nothing, so they hear no fold cues at all.
  //
  // FOLDS ARE AUDIBLE AGAIN — same sample, quieter arithmetic. The rule above
  // silenced every fold but your own because of the NOISE, not the event, and
  // the noise now has numbers. fold.wav measures −31.0 (RMS of its loudest
  // 200ms window) — the WHISPER tier, the quiet end of the measured ladder in
  // constants/audioConstants.ts. And new in this change, an opponent's fold
  // additionally rides the OPPONENT_ACTION_VOLUME handle in pages/Game.tsx
  // (0.3, i.e. −10.5 dB), landing near −41.5: under every figure in the
  // ladder. At that level the event is worth having back — a table where
  // nobody's folds are audible reads as dead rather than calm (owner, 8/26).
  // A re-cut card-slide-on-felt sample remains a candidate for a future
  // asset-only swap; it is deliberately NOT in this change (dev review #3747
  // — no such asset exists in the tree).
  //
  // It rides the ordinary gates rather than a special case: the active-table
  // gate in useSound means only the table the player is watching makes this
  // noise at all (EN-10577), and shouldPlayTableSfx below still honours "Mute
  // Other Players' Sounds" for other seats' folds. Observers hear the table
  // they are watching, which is the point of watching it.
  //
  // If opponent folds turn out to be intrusive even at this level, restoring
  // the old behaviour is a one-line revert to `if (!ownFold) return null;` —
  // keep that in mind before adding conditions here.

  const allowed = shouldPlayTableSfx({
    origin: 'player',
    actorSeatId: lastplayer,
    currentPlayerSeatId,
    tableSoundsEnabled,
    muteOtherPlayersSounds,
    muteOtherPlayersIsExplicit
  });

  return allowed ? cue : null;
};

/** The two chip samples a wager can ride. */
export type ChipCue = 'smallBet' | 'betChips';

/**
 * Relative tolerance for "equal to the big blind".
 *
 * Equality is the single most important case here — a limp, the big blind
 * itself, a min bet — and it is the one floating point can lose. Stakes are
 * decimal ($0.02/$0.05) and the two numbers reach this function from different
 * places (a seat's `betDisplay`, often a string, versus the table's `bb`), so a
 * call of exactly the blind can land a hair above it and read as a raise.
 *
 * Relative rather than absolute so it scales from $0.02 to a 500k tournament
 * blind, and far below any real difference in chips — double precision error at
 * these magnitudes is ~1e-16 relative.
 */
const CHIP_CUE_EPSILON = 1e-9;

/**
 * Which chip sample a wager gets: the single chip, or the handful.
 *
 * Anything AT OR UNDER the table's big blind is one chip in life — the blinds
 * themselves, a limp, a min bet, a call of a min bet — so it gets the
 * single-chip sample. Everything above it keeps the multi-chip `betChips`.
 *
 * The point is a table you can read with your ears (owner, 8/28): a limped pot
 * and a raised pot used to sound identical, because every wager of any size got
 * the same handful of chips. Now the difference between a field limping in to
 * see a flop and somebody actually betting is audible without looking.
 *
 * The threshold is the TABLE'S big blind, never a fixed amount, so the rule
 * means the same thing at every stake — and it follows a tournament up through
 * its blind levels on its own.
 *
 * Scope: this chooses between two samples for a WAGER. It has nothing to do
 * with `chipsToPot` (bets swept in at the end of a round) or `collectChips`
 * (the winner taking the pot), which are different events and keep their own
 * cues at any size.
 *
 * Anything unusable — no blind on the table yet, a zero or malformed amount —
 * falls back to `betChips`, which is exactly the behaviour that shipped before
 * this cue existed. A missing field must never cost the table a sound.
 */
export const resolveChipCue = ({
  amount,
  bigBlind
}: {
  amount: number | string | null | undefined;
  bigBlind: number | string | null | undefined;
}): ChipCue => {
  const bet = Number(amount);
  const bb = Number(bigBlind);
  if (!Number.isFinite(bet) || bet <= 0) return 'betChips';
  if (!Number.isFinite(bb) || bb <= 0) return 'betChips';
  return bet <= bb + Math.abs(bb) * CHIP_CUE_EPSILON ? 'smallBet' : 'betChips';
};

/**
 * Which action string to believe for the cue.
 *
 * Normally the per-seat field, `p{lastplayer}action` — it is the accurate one,
 * and it is what the fold path writes.
 *
 * The fallback exists because `lastplayer` is not guaranteed to arrive intact:
 * the Dynamo adapter coerces a missing value to an empty string
 * (`lastplayer: dynamoData.gameState?.lastplayer || ""`,
 * holdem-processor/lib/dynamoTableAdapter.js:278). With `lastplayer === ''` the
 * seat lookup becomes `game['paction']` — undefined — and every action cue goes
 * silent with no error. In that case fall back to the table-level `lastaction`.
 *
 * The fallback is deliberately NOT the primary: the fold SQL
 * (`updateTableAfterPlayerFolds`) does not write `lastaction`, so it can be
 * stale, and preferring it would announce a fold as the previous check. Used
 * only when the seat is genuinely unresolvable, so the normal path is untouched.
 */
export const resolveLastAction = ({
  lastplayer,
  seatAction,
  lastaction
}: {
  lastplayer?: number | string | null;
  /** game[`p${lastplayer}action`] */
  seatAction?: string | null;
  /** game.lastaction */
  lastaction?: string | null;
}): string | null => {
  const seatIsResolvable =
    lastplayer !== null &&
    lastplayer !== undefined &&
    String(lastplayer).trim() !== '' &&
    Number.isFinite(Number(lastplayer));

  return (seatIsResolvable ? seatAction : lastaction) ?? null;
};

/** Max seats we scan for per-seat action fields (`p1action`..`p10action`). */
export const MAX_SEATS = 10;

/** Community-card fields on the table row, in board order. */
export const BOARD_CARD_KEYS = ['card1', 'card2', 'card3', 'card4', 'card5'] as const;

/**
 * How many community cards are currently on the board.
 *
 * The board cue is driven off THIS, not off the `hand` phase, because the phase
 * is not what puts a card on screen — `card1`..`card5` are. In an all-in run-out
 * the hand races past 13, which makes `HoldEmCards` pass `skipFlip` for every
 * card (`i < newCardStartIndex || handNum >= 13`), so the whole board snaps in at
 * once while the phase-keyed cues for flop/turn/river trail in behind it. Hence
 * the reported lag, and hence why it only showed up on all-in hands.
 *
 * Counting the revealed cards instead emits the cue in the same update that
 * renders the card, whether it flips or snaps.
 */
/**
 * Which board stage is on screen: -1 none, 0 flop, 1 turn, 2 river.
 *
 * Mirrors `HoldEmCards`' live dealing-stage latch, because that — not the card
 * data alone — is what puts a newly dealt community card on screen:
 *
 *   if (handNum >= 6 && handNum < 13) {
 *     if (handNum >= 10) maxStage = 2; else if (handNum >= 8) 1; else 0;
 *   }
 *   if (maxStage >= 0 && game.card1) { ...render flop... }
 *
 * The cue was previously driven off the card COUNT, but `card1`..`card5` land in
 * the payload BEFORE the hand advances — HoldEmCards notes that a `hand='6'`
 * broadcast can still carry the PREVIOUS hand's flop. So a count-driven cue
 * fired ahead of the render, drifted independently of it, and the gap compounded
 * across an all-in run-out. It showed on web and not on native because the two
 * have different batching.
 *
 * Deriving the cue stage from `hand` keeps the cue on the same signal as live
 * card reveals. Reading it as a stage rather than testing `hand === 6 | 8 | 10`
 * also survives a skipped phase: a batched jump from 5 to 8 still registers as a
 * stage advance. HoldEmCards has an additional no-cue catch-up for coalesced
 * showdown snapshots (hand >= 13 with card1..card5 present), so a dropped river
 * render still shows the river without playing a late deal cue at showdown.
 */
/**
 * Showdown step from which the pot→seat chips are in flight.
 *
 * `Pot.tsx` only computes a paid-out pot once `+showdown.currentStep > 2`, and
 * `useAnimatedWinStack` documents the same gate as the moment `WinChips` mounts:
 * "The winners data lands a few showdown-steps BEFORE the chips launch… With it,
 * both fire on the same frame."
 *
 * That is the frame the pot cue belongs on. Keying it on the hand phase put it on
 * FIND_WINNERS (13) — the winner DECLARATION — which is a visibly different
 * moment from the chips moving.
 */
export const POT_CHIP_FLIGHT_MIN_STEP = 3;

/**
 * Identity of a single pot→seat chip flight, or null when none is in flight.
 *
 * Includes the pot index and the Hi/Lo phase because each is its own flight: the
 * engine awards side pots in sequence, and flips `handType` Hi→Lo on the SAME
 * pot, so a scooped Hi-Lo pot flies chips twice. One cue per flight.
 */
export const potChipFlightKey = ({
  currentStep,
  currentPot,
  handType
}: {
  currentStep?: number | string | null;
  currentPot?: number | string | null;
  handType?: string | null;
}): string | null => {
  const step = Number(currentStep);
  if (!Number.isFinite(step) || step < POT_CHIP_FLIGHT_MIN_STEP) return null;
  if (currentPot === null || currentPot === undefined || String(currentPot).trim() === '') {
    return null;
  }
  return `${currentPot}:${handType ?? ''}`;
};

/**
 * Whether this table's subtree may PAINT a celebration (confetti).
 *
 * The distinction that matters here is VISIBLE, not focused. `isActiveTable`
 * means "the table the player is on" — exactly one table however many are
 * mounted — and it is the right gate for anything that talks to the engine or
 * makes noise. It is the WRONG gate for a visual: in the multi-table grid, and
 * in a tiled carousel, several felts share the viewport and the player is
 * looking at all of them, so gating a celebration on it meant only the selected
 * cell could ever celebrate and a win at any other visible table was silently
 * swallowed (reported — "confetti animation is not working on Multitable grid
 * view").
 *
 * `gridMode` is true for every grid cell and every tiled carousel panel, which
 * is the set of felts on screen at once. In single-table view it is false, so
 * the active-table gate stands alone exactly as before and the carousel's
 * keep-alive panels — mounted, parked off-screen, `visibility: hidden` — stay
 * dark rather than burning frames on an animation nobody can see.
 *
 * Known, bounded cost: while TILED, a keep-alive panel is also `gridMode`, so a
 * win at a table the player has swiped away from animates where it cannot be
 * seen. Distinguishing those would mean threading per-panel visibility through
 * RebindingTableView's memo boundary, which exists precisely to stop felt
 * subtrees re-rendering during a swipe — not a trade worth making for one
 * bounded 6s canvas per winning hand.
 */
export const canPaintTableCelebration = ({
  isActiveTable,
  gridMode
}: {
  isActiveTable: boolean;
  gridMode: boolean;
}): boolean => isActiveTable || gridMode;

/**
 * Showdown step from which the WINNER BANNER is visible for the current pot.
 *
 * `getWinnersPot` (utils/functions.ts) returns falsy for `currentStep < 2` and
 * the pot's winners row for `currentStep >= 2` — the moment `isWinner`,
 * `isHighWinner`, `isLowWinner` and `isWinningCard` start returning true, i.e.
 * the frame the WINNER pill appears on the winning seat. That is one step
 * earlier than the chip flight (step 3) — the pill appears with the winning
 * cards, THEN chips fly. Confetti belongs on the DECLARATION, not the flight.
 */
export const POT_WINNER_DECLARATION_MIN_STEP = 2;

/**
 * Identity of a single pot's WINNER DECLARATION, or null before it appears.
 *
 * Sibling of `potChipFlightKey` — same shape (`{pot}:{handType}`), same
 * per-flight semantics (side pots each get their own key, and a scooped Hi/Lo
 * pot flips handType Hi→Lo for a second key), but activates one step earlier
 * so callers can sync to the WINNER PILL rather than to the chip animation.
 * The key stays the same across steps 2 → 3 → …, so an effect that fires on
 * the transition never re-fires when the flight starts.
 */
export const potWinnerDeclarationKey = ({
  currentStep,
  currentPot,
  handType
}: {
  currentStep?: number | string | null;
  currentPot?: number | string | null;
  handType?: string | null;
}): string | null => {
  const step = Number(currentStep);
  if (!Number.isFinite(step) || step < POT_WINNER_DECLARATION_MIN_STEP) return null;
  if (currentPot === null || currentPot === undefined || String(currentPot).trim() === '') {
    return null;
  }
  return `${currentPot}:${handType ?? ''}`;
};

/**
 * Seats being paid by the pot distribution currently on screen — the pot named
 * by `showdown.currentPot`, in the Hi/Lo phase named by `showdown.handType`.
 *
 * Used to attribute the pot cue: if the local player is among them the cue is
 * their OWN, otherwise it belongs to an opponent and the "Mute Other Players'
 * Sounds" gate applies. Unity does the same — its payout `CHIPSTOPOT` is emitted
 * per player with `isOwnSound: IsPOVPlayer()` (UIPlayer.OnStackUpdated). The
 * confetti burst is attributed the same way.
 *
 * PHASE-SCOPED, because a Hi-Lo pot pays out TWICE. The engine awards the Hi
 * half, then (only if that pot has qualifying low hands) flips
 * `showdown.handType` Hi → Lo and replays steps 1-4 for the Lo half — see
 * `showdownMoveNext` in omaha-processor/omaha/engine.js. `pot.winners` holds
 * BOTH halves at once, each row tagged with its own `handType`, so returning
 * every row made each half's payout look like it paid everyone: both the Hi and
 * the Lo winner were celebrated twice, once per phase (reported — "Hi-Lo winners
 * show double confetti on every pot distribution"). Matching the row's tag
 * against the phase gives one burst per winner, on their own half.
 *
 * Rows are also gated on `win > 0`: this is the same predicate the WINNER glow
 * (useSeatLogic), `isWinner` (utils/functions) and Pot.tsx's WinChips filter
 * already use, so the celebration, the sound, the glow and the flying chips all
 * agree on who is being paid right now. Winners in a non-Hi-Lo game carry no
 * `handType`, so `!w.handType` keeps them phase-agnostic — Texas is unaffected.
 *
 * `pots` is INDEX-accessed on purpose: the engine ships it as a numeric-keyed
 * object, so `Array.isArray` / `for..of` would wrongly read it as empty (the same
 * trap `seatWinTotal` documents in useAnimatedWinStack). `winners` is normalised
 * through `Object.values` for the same reason.
 */
export const potFlightWinnerSeats = (pots: any, showdown: any): number[] => {
  const currentPot = showdown?.currentPot;
  if (!pots || currentPot === null || currentPot === undefined) return [];

  const winners = pots[currentPot]?.winners;
  if (!winners) return [];

  const phase = showdown?.handType;
  const list: any[] = Array.isArray(winners) ? winners : Object.values(winners);
  const seats: number[] = [];
  list.forEach((w) => {
    const seat = Number(w?.player);
    if (!Number.isFinite(seat) || seats.includes(seat)) return;
    if (!(Number(w?.win) > 0)) return;
    if (w?.handType && w.handType !== phase) return;
    seats.push(seat);
  });
  return seats;
};

/**
 * Hand phase from which a hand's winner is settled enough to celebrate.
 *
 * FIND_WINNERS (13) in the engine's table — the winner is DECLARED there, which
 * is the right moment for the visual celebration. Kept at 13 deliberately: the
 * confetti has always fired there and retiming it is not this ticket's business.
 *
 * The pot AUDIO is a separate thing and deliberately does NOT use this: it rides
 * the chip flight instead (see `potChipFlightKey`), because 13 is the winner
 * declaration and the chips move later.
 *
 * Compared with `>=` so a batched burst collapsing 13→15 still celebrates.
 */
export const WIN_CELEBRATION_MIN_HAND = 13;

/**
 * Whether the win celebration should fire on this update, and the next armed state.
 *
 * Replaces an inline guard that consumed the winners transition too eagerly:
 *
 *   const prev = ref.current;
 *   const cur = winners?.length || 0;
 *   ref.current = cur;                       // <-- unconditional
 *   if (prev === 0 && cur > 0 && hand >= 13) { ...fire... }
 *
 * `winners` can land while `hand` is still 11/12 — the reducer merges payloads
 * partially and bursts get batched — and the ref was advanced regardless. That
 * consumed the 0 -> N transition before the phase caught up, so by the time
 * `hand` reached 13 the guard could never match and the cue never played.
 *
 * Here the state only advances when the cue actually fires, or when a hand is in
 * progress (which re-arms for the next win). Waiting for the phase keeps the
 * transition intact instead of burning it.
 */
export const resolveWinCelebration = ({
  hand,
  winnerCount,
  alreadyFired
}: {
  hand: number | string | null | undefined;
  winnerCount: number;
  alreadyFired: boolean;
}): { fire: boolean; nextFired: boolean } => {
  const phase = Number(hand);
  const atWin = Number.isFinite(phase) && phase >= WIN_CELEBRATION_MIN_HAND;

  // A hand is in progress (or a new one started): arm for the coming win.
  if (!atWin) return { fire: false, nextFired: false };
  // Already sounded for this hand — winners stay set through 13/14/15.
  if (alreadyFired) return { fire: false, nextFired: true };
  // Winner phase but nothing recorded yet: stay armed rather than consuming the edge.
  if (winnerCount <= 0) return { fire: false, nextFired: false };

  return { fire: true, nextFired: true };
};

export const boardStageFromHand = (hand: number | string | null | undefined): number => {
  const phase = Number(hand);
  if (!Number.isFinite(phase) || phase < 6 || phase >= 13) return -1;
  if (phase >= 10) return 2;
  if (phase >= 8) return 1;
  return 0;
};

export const countBoardCards = (game: Record<string, any> | null | undefined): number => {
  if (!game) return 0;
  return BOARD_CARD_KEYS.reduce((n, key) => (String(game[key] ?? '').trim() ? n + 1 : n), 0);
};

/**
 * Snapshot of every seat's action field, as a stable string.
 *
 * This is the trigger of last resort, and it is the reliable one. The cue used to
 * be driven by `lastplayer` / `lastmove`, on the assumption that the server bumps
 * them per action. It does bump them in MySQL — but the Dynamo enrichment path
 * coerces a missing `lastplayer` to `""`
 * (holdem-processor/lib/dynamoTableAdapter.js:278), and when those fields arrive
 * static the effect keyed on them never re-runs and the de-dupe key never
 * changes. Symptom: the first cue of a session plays and every later one is
 * swallowed, mute setting irrelevant.
 *
 * `p{n}action` is per seat and is what actually moves — `Bet.tsx` has always
 * driven its chip cues off per-seat fields, and those work. Diffing this
 * signature also yields the acting seat directly, so the mute gate no longer
 * needs `lastplayer` to attribute a cue.
 */
export const seatActionsSignature = (
  game: Record<string, any> | null | undefined,
  maxSeats: number = MAX_SEATS
): string => {
  if (!game) return '';
  const parts: string[] = [];
  for (let seat = 1; seat <= maxSeats; seat += 1) {
    parts.push(String(game[`p${seat}action`] ?? '').toLowerCase());
  }
  return parts.join('|');
};

export type SeatAction = { seatId: number; action: string };

/**
 * Which seat's action just changed, comparing two {@link seatActionsSignature}
 * snapshots. Returns null when nothing changed or the change was a CLEAR (a new
 * hand blanking the fields), which must not sound.
 *
 * When several seats change in one payload — a batched burst — the last one is
 * reported: it is the most recent action, and reporting one keeps a burst from
 * emitting a pile of overlapping cues.
 */
export const diffSeatAction = (prev: string, next: string): SeatAction | null => {
  if (!next || prev === next) return null;
  const before = prev.split('|');
  const after = next.split('|');
  let found: SeatAction | null = null;
  for (let i = 0; i < after.length; i += 1) {
    const action = after[i];
    // Only a transition INTO an action counts; blanking is a hand reset.
    if (action && action !== before[i]) found = { seatId: i + 1, action };
  }
  return found;
};

export type ActionSfxEvent = {
  /** game.gameNo — the hand instance. */
  gameNo?: number | string | null;
  /** game.lastmove — the server stamps this per ACTION (`lastmove = time + 1`). */
  lastmove?: number | string | null;
  lastplayer?: number | string | null;
  cue: ActionSfx;
};

/**
 * Identity of a single player action, used to fire its cue exactly once.
 *
 * The sound effect re-runs on every `hand` step and whenever `playSound`'s
 * identity changes, and the payload keeps carrying the last action across those
 * runs — so without a key the same check would sound two or three times.
 *
 * `lastmove` is what makes this an ACTION identity rather than a state identity.
 * Keying on (hand instance, seat, cue) alone looked sufficient but silently broke
 * checks: a player legitimately checks on the flop AND the turn AND the river,
 * which is the same seat and the same cue inside the same hand, so every check
 * after the first was swallowed. The server bumps `lastmove` on each action
 * (see `updateTableAfterPlayerFolds` / the check update in sql/shared/updates),
 * so it distinguishes those while still collapsing pure re-renders of one action.
 *
 * `hand` is deliberately NOT part of the key: a closing action advances the phase
 * while still on the payload (check at hand 5 → flop deal at hand 6 with
 * `p{n}action` still 'check'), so including it would re-fire that same cue.
 */
export const actionSfxEventKey = ({ gameNo, lastmove, lastplayer, cue }: ActionSfxEvent): string =>
  `${gameNo ?? ''}:${lastmove ?? ''}:${lastplayer ?? ''}:${cue}`;
